import type { ReactNode } from 'react';
import { WidgetSkeleton } from './Skeleton';
import styles from './WidgetContainer.module.scss';

export interface WidgetContainerProps {
  title: string;
  description?: string;
  children: ReactNode;
  isLoading?: boolean;
  error?: Error | null;
  isEmpty?: boolean;
  emptyMessage?: string;
  actions?: ReactNode;
  size?: 'small' | 'medium' | 'large';
}

/**
 * Reusable Widget Container Component
 *
 * Provides consistent styling, loading states, error handling, and empty states
 * for all analytics widgets. Wraps widget content with header, actions, and states.
 *
 * @example
 * <WidgetContainer
 *   title="Overall Success Rate"
 *   description="Your performance across all quizzes"
 *   isLoading={isLoading}
 *   error={error}
 *   isEmpty={!data}
 * >
 *   <OverallSuccessRateContent data={data} />
 * </WidgetContainer>
 */
export function WidgetContainer({
  title,
  description,
  children,
  isLoading = false,
  error = null,
  isEmpty = false,
  emptyMessage = 'No data available',
  actions,
  size = 'medium',
}: WidgetContainerProps) {
  return (
    <div className={`${styles.widgetContainer} ${styles[`widgetContainer--${size}`]}`}>
      {/* Header */}
      <div className={styles.widgetContainer__header}>
        <div className={styles.widgetContainer__titleGroup}>
          <h3 className={styles.widgetContainer__title}>{title}</h3>
          {description && (
            <p className={styles.widgetContainer__description}>{description}</p>
          )}
        </div>
        {actions && <div className={styles.widgetContainer__actions}>{actions}</div>}
      </div>

      {/* Content */}
      <div className={styles.widgetContainer__content}>
        {isLoading ? (
          <WidgetSkeleton showHeader={false} linesCount={size === 'large' ? 4 : 3} />
        ) : error ? (
          <div className={styles.widgetContainer__errorState}>
            <svg
              className={styles.widgetContainer__errorIcon}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className={styles.widgetContainer__errorText}>
              {error.message || 'Failed to load data'}
            </p>
          </div>
        ) : isEmpty ? (
          <div className={styles.widgetContainer__emptyState}>
            <svg
              className={styles.widgetContainer__emptyIcon}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <p className={styles.widgetContainer__emptyText}>{emptyMessage}</p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
